import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {components} from '@/lib/api-schema'

/** Se direcciona por `clave`, nunca por `id` (cambia con cada versión); escribir sobre una
 * clave existente lleva `version_base` y el backend contesta 409 si ya no es la vigente. */

export type CatalogoPerfil = components['schemas']['CatalogoPerfilRead']
export type CatalogoCondicion = components['schemas']['CatalogoCondicionRead']
export type CatalogoCondicionEditada = components['schemas']['CatalogoCondicionEditadaRead']
export type CatalogoTermino = components['schemas']['CatalogoTerminoRead']
export type CatalogoVacios = components['schemas']['VaciosRead']
export type CatalogoVersion = components['schemas']['VersionCatalogoRead']
export type TerminoFamilia = CatalogoTermino['familia']
export type CatalogoEstado = 'vigentes' | 'archivadas' | 'todas'

type PerfilesPage = components['schemas']['Page_CatalogoPerfilRead_']
type CondicionesPage = components['schemas']['Page_CatalogoCondicionRead_']
type TerminosPage = components['schemas']['Page_CatalogoTerminoRead_']

export const CATALOGO_PAGE_SIZE = 20

const FAMILIAS = ['perfiles', 'condiciones', 'terminos'] as const
export type Familia = (typeof FAMILIAS)[number]

type ListParams = {page: number; search: string; estado: CatalogoEstado; familia?: TerminoFamilia}

function useLista<T>(familia: Familia, params: ListParams, enabled: boolean) {
  return useQuery({
    queryKey: ['admin-catalogo', familia, params],
    queryFn: async () =>
      (
        await api.get<T>(`/admin/catalogo/${familia}`, {
          params: {
            page: params.page,
            page_size: CATALOGO_PAGE_SIZE,
            search: params.search || undefined,
            estado: params.estado,
            familia: params.familia,
          },
        })
      ).data,
    enabled,
    placeholderData: (previous) => previous,
  })
}

export function useCatalogoPerfiles(params: ListParams, enabled: boolean) {
  return useLista<PerfilesPage>('perfiles', params, enabled)
}

export function useCatalogoCondiciones(params: ListParams, enabled: boolean) {
  return useLista<CondicionesPage>('condiciones', params, enabled)
}

export function useCatalogoTerminos(params: ListParams, enabled: boolean) {
  return useLista<TerminosPage>('terminos', params, enabled)
}

export function useCatalogoVacios(enabled: boolean) {
  return useQuery({
    queryKey: ['admin-catalogo', 'vacios'],
    queryFn: async () => (await api.get<CatalogoVacios>('/admin/catalogo/vacios')).data,
    enabled,
  })
}

export function useCatalogoVersion(enabled: boolean) {
  return useQuery({
    queryKey: ['admin-catalogo', 'version'],
    queryFn: async () => (await api.get<CatalogoVersion>('/admin/catalogo/version')).data,
    enabled,
  })
}

/** Invalida todo `admin-catalogo`: una edición cambia las vistas del filtro, los vacíos y el sello. */
function useEscritura<TVars, TData>(mutationFn: (vars: TVars) => Promise<TData>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['admin-catalogo']}),
  })
}

export type PerfilCuerpo = {
  cargo_nombre: string
  ciiu_prefijos: string[]
  clases_riesgo: number[]
  funciones: string[]
  peligros: string[]
  condiciones: string[]
  requerimientos: string[]
}

export type CondicionCuerpo = {
  condicion: string
  tipo: string
  nombre: string
  norma_nombre: string | null
  norma_articulo: string | null
  relacion_norma: string | null
  horas: number | null
  vigencia_meses: number | null
  destino: string | null
}

export type TerminoCuerpo = {
  familia: TerminoFamilia
  ancla: string | null
  terminos: string[]
}

export function useCrearPerfil() {
  return useEscritura(
    async (vars: {clave: string} & PerfilCuerpo) =>
      (await api.post<CatalogoPerfil>('/admin/catalogo/perfiles', vars)).data
  )
}

export function useEditarPerfil() {
  return useEscritura(async (vars: {clave: string; version_base: number} & PerfilCuerpo) => {
    const {clave, ...body} = vars
    return (await api.put<CatalogoPerfil>(`/admin/catalogo/perfiles/${clave}`, body)).data
  })
}

export function useCrearCondicion() {
  return useEscritura(
    async (vars: {clave: string} & CondicionCuerpo) =>
      (await api.post<CatalogoCondicion>('/admin/catalogo/condiciones', vars)).data
  )
}

export function useEditarCondicion() {
  return useEscritura(async (vars: {clave: string; version_base: number} & CondicionCuerpo) => {
    const {clave, ...body} = vars
    return (await api.put<CatalogoCondicionEditada>(`/admin/catalogo/condiciones/${clave}`, body)).data
  })
}

export function useCrearTermino() {
  return useEscritura(
    async (vars: {clave: string} & TerminoCuerpo) =>
      (await api.post<CatalogoTermino>('/admin/catalogo/terminos', vars)).data
  )
}

export function useEditarTermino() {
  return useEscritura(async (vars: {clave: string; version_base: number} & TerminoCuerpo) => {
    const {clave, ...body} = vars
    return (await api.put<CatalogoTermino>(`/admin/catalogo/terminos/${clave}`, body)).data
  })
}

/** Firmar no manda nombre ni fecha: los pone el backend con el usuario autenticado. */
export function useAccionCatalogo(accion: 'archivar' | 'restaurar' | 'firmar') {
  return useEscritura(
    async (vars: {familia: Familia; clave: string; version_base: number}) =>
      (
        await api.post(`/admin/catalogo/${vars.familia}/${vars.clave}/${accion}`, {
          version_base: vars.version_base,
        })
      ).data
  )
}

/** Borrado físico: solo procede sobre una clave que nadie citó; si no, 409 y hay que archivar. */
export function useBorrarCatalogo() {
  return useEscritura(async (vars: {familia: Familia; clave: string}) => {
    await api.delete(`/admin/catalogo/${vars.familia}/${vars.clave}`)
  })
}
